import {
  Autorenew,
  ContentPaste,
  Delete,
  Description,
  Edit,
  FileDownload,
  FileUpload,
  KeyboardArrowDown,
  Visibility,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  styled,
  TableCell,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import { useRef, useState, useEffect } from "react";
import { isAndroid, isMobile, isMobileSafari } from "react-device-detect";
import { toast } from "react-toastify";
import { useInterventionsContext } from "../../../../../contexts/InterventionsContext";
import { useLocationContext } from "../../../../../contexts/LocationContext";
import { usePractitionerRoleContext } from "../../../../../contexts/PractitionerRoleContext";
import { useSessionContext } from "../../../../../contexts/SessionContext";
import { Base64SignPDF } from "../../../../../services/dsig";
import { GetAppointment, GetPatient } from "../../../../../services/fhir";
import { GetDocumentReferenceById } from "../../../../../services/fhir/documentreference/GetDocumentReferenceById";
import { GetBinary } from "../../../../../services/fhir/patientsummary/Binary/GetBinary";
import { PostDocumentReference } from "../../../../../services/fhir/patientsummary/documentreference/PostDocumentReference";
import { PatchEHRPoster } from "../../../../../services/fhir/patientsummary/PatchEHRPoster";
import { useFhirData } from "../../../../../utils/useFhirData";
import { ConfirmDocumentDialog } from "../../../../examsolicitation/components/ConfirmDocumentDialog";
import { verifyDocumentExam } from "@/services/n8n/verifyDocumentExam";
import { InvalidDocumentWarning } from "./InvalidDocumentWarning";
import { DocumentVisualization } from "@/pages/medicalcare/components/DocumentVisualization/DocumentVisualization";
import { SignatureDialog } from "@/pages/medicalcare/components/prescription/SignatureDialog";
import { downloadFile } from "@/utils/fileDownloadHelper";
import { isReactNativeWebView, selectFileRN } from "@/utils/useWebViewBridge";
import { DeleteResultsDialog } from "./DeleteResultsDialog";
import { usePreferMobileCards } from "../../../context/EhrRecordsLayoutContext";
import {
  EhrMobileActionStack,
  ehrMobileActionBtnSx,
} from "../../mobile/EhrMobileActionStack";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export function ServiceRequestActions({
  row,
  handleOpen,
  setBinaryUrl,
  setRefetch,
  openVisualizer,
  verifyVisualizer,
  cardActions,
  serviceRequests,
  setDocumentComment,
}: any) {
  const { user } = useSessionContext();
  const { access } = useSessionContext();
  const { userPractitionerRole } = usePractitionerRoleContext();
  const { location } = useLocationContext();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedResultIndex, setSelectedResultIndex] = useState<number>(0);

  const [patient, setPatient] = useState<any>();
  const [locationData, setLocationData] = useState<any>();

  const { action, selectedInterventions, setInterventionsList } =
    useInterventionsContext();

  const [originalDocument, setOriginalDocument] = useState<any>();
  const [base64, setBase64] = useState<any>();

  const [signatureDialogOpen, setSignatureDialogOpen] =
    useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [item, setItem] = useState<any>([]);

  // Detecta React Native de forma segura (com try-catch)
  const isRN = (() => {
    try {
      return isReactNativeWebView();
    } catch (e) {
      console.error("[ServiceRequestActions] Erro ao detectar RN:", e);
      return false;
    }
  })();

  const isMobileCardLayout = usePreferMobileCards();
  const useMobileLabeledActions = isMobileCardLayout || !!cardActions;

  const auxOrganization = row?.fullResource?.contained?.find((el: any) =>
    el?.resourceType?.includes("Organization")
  )?.identifier?.[0]?.value;

  const auxPractitioner = row?.fullResource?.author
    ?.find((el: any) => el?.reference?.includes("Practitioner"))
    ?.reference?.split("/")?.[1];

  const auxReplaces = `identifier=${row?.fullResource?.identifier?.[1]?.system}%7C${row?.fullResource?.identifier?.[1]?.value}`;

  // const replaces = row?.fullResource?.id;

  const objData = {
    organization: {
      id: auxOrganization,
      alias: row?.fullResource?.contained?.[1]?.alias?.[0],
      name: row?.fullResource?.contained?.[1]?.name,
    },
    practitioner: {
      id: auxPractitioner,
      name: row?.fullResource?.contained?.[0]?.name?.[0]?.given?.[0],
    },
    servicePratice: row?.fullResource?.practiceSetting?.coding?.[0],
    time: {
      start: row?.fullResource?.period?.start,
      end: row?.fullResource?.period?.end,
    },
    relatesTo: auxReplaces,
    codeValue: row?.fullResource?.type?.coding?.[0]?.code,
    codeDisplay: row?.fullResource?.type?.coding?.[0]?.display,
    categoryValue: row?.fullResource?.category?.[0]?.coding?.[0]?.code,
    categoryDisplay: row?.fullResource?.category?.[0]?.coding?.[0]?.display,
    description: row?.fullResource?.description,
  };

  const basedOn = row?.fullResource?.basedOn
    ?.map(
      (el: any) =>
        `<fhir:basedOn>
    <fhir:reference value="${el?.reference}"/>
    </fhir:basedOn>
    `
    )
    ?.join("");

  const encounter = {
    id: row?.fullResource?.context
      ?.find((el: any) => el?.reference?.includes("Encounter"))
      ?.reference?.split("/")?.[1],

    appointmentId: row?.fullResource?.context
      ?.find((el: any) => el?.reference?.includes("Appointment"))
      ?.reference?.split("/")?.[1],
    requester: {
      reference: `PractitionerRole/${userPractitionerRole?.id}`,
      display: row?.fullResource?.contained?.[0]?.name?.[0]?.given?.[0],
    },
    subject: row?.fullResource?.subject?.reference?.split("-")?.[1],
  };

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const resolveDocumentReferenceIdFromUrl = (docUrl?: string): string | undefined => {
    try {
      if (!docUrl) return undefined;
      const byMatch = row?.auxResults?.find(
        (e: any) => e?.resource?.content?.[0]?.attachment?.url === docUrl
      )?.resource?.id;
      if (byMatch) {
        return byMatch;
      }
      if (docUrl.includes("DocumentReference/")) {
        const after = docUrl.split("DocumentReference/")?.[1];
        const docId = after?.split("/")?.[0]?.split("?")?.[0];
        if (docId) {
          return docId;
        }
      }
      const parts = docUrl.split("/");
      const last = parts[parts.length - 1]?.split("?")?.[0];
      console.warn("[ServiceRequestActions] fallback id (provavelmente Binary):", last);
      return last;
    } catch (_) {}
    return undefined;
  };

  const setCommentFromDocId = (docId?: string) => {
    try {
      if (!docId) return;
      if (Array.isArray(serviceRequests) && setDocumentComment) {
        console.log("[ServiceRequestActions] setCommentFromDocId ->", docId);
        const sr = serviceRequests?.find((e: any) =>
          e?.resource?.identifier?.some(
            (id: any) => id?.system === "urn:ietf:rfc:3986" && id?.value === `${docId}.note.1`
          )
        )?.resource;
        const noteField = Array.isArray(sr?.note) ? sr?.note?.[0] : sr?.note;
        const noteText = noteField?.text || "";
        setDocumentComment(noteText);
      }
    } catch (_) {}
  };

  /**
   * MANIPULA CLIQUE NO BOTÃO DE UPLOAD
   *
   * Detecta automaticamente o ambiente:
   * - Browser: Clica no input file escondido
   * - React Native: Abre seletor nativo de arquivos
   */
  const handleClickUpload = (e?: React.MouseEvent) => {
    // Para a propagação do evento para evitar triggers indesejados
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (row?.resultUrls && row.resultUrls.length >= 5) {
      toast.warn("Limite máximo de 5 resultados por solicitação atingido.");
      return;
    }

    console.log("╔═══════════════════════════════════════════════╗");
    console.log("║  handleClickUpload CHAMADO                    ║");
    console.log("╚═══════════════════════════════════════════════╝");
    console.log("[ServiceRequestActions] Click upload - isRN:", isRN);

    try {
      if (isRN) {
        // React Native: usa seletor nativo
        console.log("[ServiceRequestActions] Solicitando arquivo ao RN...");
        selectFileRN(".pdf", async (result) => {
          console.log(
            "[ServiceRequestActions] Arquivo recebido do RN:",
            result.fileName
          );

          // Inicia análise do documento
          setIsAnalyzingDocument(true);
          toast.info("Verificando documento...");

          try {
            // Converte dataURL para File para verificação
            // (verifyDocumentExam espera um File)
            const response = await fetch(result.base64);
            const blob = await response.blob();
            const file = new File([blob], result.fileName, {
              type: result.mimeType,
            });

            const verificationResult = await verifyDocumentExam(file);

            if (verificationResult?.status === "success") {
              setInvalidDocumentBase64(null);
              setShowInvalidDocumentWarning(false);
              setFileBase64(result.base64);
            } else {
              setInvalidDocumentBase64(result.base64);

              if (access?.type === "professional") {
                setShowInvalidDocumentWarning(false);
                setFileBase64(result.base64);
              } else {
                setFileBase64(null);
                setTimeout(() => {
                  setShowInvalidDocumentWarning(true);
                }, 100);
                console.log("O arquivo não é válido");
              }
            }
          } catch (error) {
            console.error("Erro ao verificar documento:", error);
            toast.error(
              "Ocorreu um erro ao processar o documento. Tente novamente."
            );
          } finally {
            setIsAnalyzingDocument(false);
          }
        });
      } else {
        // Browser: clica no input escondido
        console.log("[ServiceRequestActions] Browser - clicando no input file");
        if (fileInputRef.current) {
          if (fileInputRef.current.value) {
            fileInputRef.current.value = "";
          }
          fileInputRef.current.click();

          const timeoutId = setTimeout(() => {
            if (isAnalyzingDocument && !fileInputRef.current?.files?.length) {
              setIsAnalyzingDocument(false);
            }
          }, 1000);

          return () => clearTimeout(timeoutId);
        }
      }
    } catch (error) {
      console.error(
        "[ServiceRequestActions] Erro no handleClickUpload:",
        error
      );
      setIsAnalyzingDocument(false);
    }
  };

  const convertToBase64 = (file: File) => {
    if (file) {
      return new Promise((resolve, reject) => {
        const fileReader = new FileReader();

        fileReader.readAsDataURL(file);

        fileReader.onload = () => {
          resolve(fileReader.result);
        };

        fileReader.onerror = (error) => {
          reject(error);
        };
      });
    }
  };

  const [fileBase64, setFileBase64] = useState<any>();
  const [invalidDocumentBase64, setInvalidDocumentBase64] = useState<any>();
  const [showInvalidDocumentWarning, setShowInvalidDocumentWarning] =
    useState<boolean>(false);
  const [isAnalyzingDocument, setIsAnalyzingDocument] =
    useState<boolean>(false);
  
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState<boolean>(false);
  const [resultToDelete, setResultToDelete] = useState<string | undefined>();

  const uploadImage = async (e: any) => {
    const file = e.target.files[0];
    if (!file) return;

    // Verificar novamente se já atingiu o limite de 5 resultados
    if (row?.resultUrls && row.resultUrls.length >= 5) {
      toast.warn("Limite máximo de 5 resultados por solicitação atingido.");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    try {
      setIsAnalyzingDocument(true);
      toast.info("Verificando documento...");

      const result = await verifyDocumentExam(file);

      const base64 = await convertToBase64(file);

      if (result?.status === "success") {
        setInvalidDocumentBase64(null);
        setShowInvalidDocumentWarning(false);
        setFileBase64(base64);
      } else {
        setInvalidDocumentBase64(base64);

        if (access?.type === "professional") {
          setShowInvalidDocumentWarning(false);
          setFileBase64(base64);
        } else {
          setFileBase64(null);

          setTimeout(() => {
            setShowInvalidDocumentWarning(true);
          }, 100);

          console.log("O arquivo não é válido");
        }
      }
    } catch (error) {
      console.error("Erro ao verificar documento:", error);
      toast.error("Ocorreu um erro ao processar o documento. Tente novamente.");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } finally {
      setIsAnalyzingDocument(false);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleResultSelect = (index: number) => {
    setSelectedResultIndex(index);
    handleMenuClose();
  };

  const docReferenceData = {
    servicePratice: {
      code: row?.fullResource?.practiceSetting?.coding?.[0]?.code,
      system: row?.fullResource?.practiceSetting?.coding?.[0]?.system,
      display: row?.fullResource?.practiceSetting?.coding?.[0]?.display,
    },
    time: {
      start: dayjs().toISOString(),
      end: dayjs().toISOString(),
    },
  };

  const refs = {
    id: row?.fullResource?.context
      ?.find((el: any) => el?.reference?.includes("Encounter"))
      ?.reference?.split("/")?.[1],
    subject:
      row?.fullResource?.subject?.reference?.split("-")?.[1] || user?.username,
    appointmentId: row?.fullResource?.context
      ?.find((el: any) => el?.reference?.includes("Appointment"))
      ?.reference?.split("/")?.[1],
  };

  const base64ToBlob = (base64: any, contentType = "") => {
    const byteCharacters = atob(base64);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  };

  /**
   * FUNÇÃO DE DOWNLOAD ATUALIZADA
   *
   * Esta função agora funciona tanto no browser quanto no React Native
   * A lógica de conversão base64 → Blob foi movida para o helper
   */
  async function downloadBase64File(binary: string) {
    console.log("╔═══════════════════════════════════════════════╗");
    console.log("║  downloadBase64File CHAMADO                   ║");
    console.log("╚═══════════════════════════════════════════════╝");
    console.log("[ServiceRequestActions] binary:", binary);

    // Proteção: não tenta fazer download se binary estiver vazio
    if (!binary || binary.trim() === "") {
      console.error(
        "[ServiceRequestActions] Erro: binary está vazio! Não há arquivo para baixar."
      );
      toast.error("Não há arquivo de solicitação disponível para download.");
      return;
    }

    try {
      const response = await GetBinary({
        urlBinary: binary,
      });

      // NOVA IMPLEMENTAÇÃO: Usa o helper que detecta o ambiente automaticamente
      downloadFile(
        response?.data, // Base64 do arquivo
        `Documento.pdf`, // Nome do arquivo
        response?.contentType // Tipo MIME
      );
    } catch (err) {
      console.error("[ServiceRequestActions] Erro ao baixar:", err);
      toast.error("Erro ao baixar arquivo. O arquivo pode não existir.");
    }
  }

  const handleSignature = async (
    provider: "vidaas" | "safeid",
    password?: string,
    lifeTime?: number
  ) => {
    try {
      const examKey = base64?.[0]
        ? Object.keys(base64[0]).find((key) => key.includes("exam"))
        : undefined;

      const base64Content = examKey
        ? base64?.[0]?.[examKey]?.base64?.split(",")?.[1]
        : undefined;

      if (!base64Content && !originalDocument) {
        throw new Error("Não foi possível encontrar o arquivo pdf");
      }

      setLoading(true);
      toast.warn("Aguardando verificação e segurança do seu provedor!");
      const signResponse = await Base64SignPDF({
        provider: provider,
        ...(provider === "safeid" && {
          userPassword: password,
        }),
        pdfBase64: base64Content ? base64Content : originalDocument,
        cpfCnpj: user?.username,
        lifeTime: lifeTime ?? 0,
      });

      if (!signResponse?.length) throw new Error("");

      const auxUuid = examKey ? base64?.[0]?.[examKey]?.uuid : undefined;

      await PostDocumentReference(
        { ...objData, base64: signResponse },
        encounter,
        basedOn,
        true,
        auxUuid
      );
      toast.success("Assinatura realizada!");
    } catch (error) {
      toast.warn(
        "Não foi possível realizar a assinatura, por favor tente novamente!"
      );

      console.error("HandleSignature error:", error);
    } finally {
      setLoading(false);
      setRefetch((prev: boolean) => !prev);
    }
  };

  async function generateItem() {
    const patient = await GetPatient({ identifier: encounter?.subject });
    setPatient(patient?.data);

    const appointment = await GetAppointment({
      ehrrunner: encounter?.subject,
      _id: encounter?.appointmentId,
    });

    const locationReference = useFhirData.findParticipant(
      appointment?.entry?.[0]?.resource?.participant,
      "Location"
    );

    const selectedLocation = location?.find((el: any) =>
      el?.resource?.id?.includes(locationReference?.reference.split("/")[1])
    )?.resource;

    setLocationData(selectedLocation);
    const exams = row?.fullServiceResource?.map((e: any) => {
      const auxItem = {
        type: "service",
        tabValue: "2",
        // inputValue: serviceList.find(
        //   (e: any) => data?.service?.code === e?.code
        // ) || {
        //   name: data?.service?.label,
        //   value: data?.service?.label,
        //   label: data?.service?.label,
        // },
        refact: true,
        name: e?.resource?.code?.concept?.coding?.[0]?.display,
        tag: "EX",
        form: "",
        note: e?.resource?.note?.[0]?.text || e?.resource?.note || "",
        condition: {
          value: e?.resource?.reason?.[0]?.concept?.coding?.[0]?.code,
          label: e?.resource?.reason?.[0]?.concept?.coding?.[0]?.display,
        },
        category: e?.resource?.category?.[0]?.coding?.[0],
      };
      return auxItem;
    });

    setItem(exams);
    exams?.map((e: any) => action(e));
    setLoading(false);
  }

  /**
   * Remove um resultado de exame específico ou todos os resultados
   * @param specificResultUrl - URL específica do resultado a ser removido (opcional)
   */
  async function removeUpload(specificResultUrl?: string) {
    try {
      // Se foi especificado um resultado específico para remover
      if (specificResultUrl) {
        const documentReference = await GetDocumentReferenceById({
          patient: user?.username,
          docId: specificResultUrl?.split("/")?.[1],
          isLogged: true,
          subjectId: user?.username,
        });

        await PatchEHRPoster({
          resourceType: "DocumentReference",
          resourceId: documentReference?.entry?.[0]?.resource?.id || "",
          operation: "replace",
          path: "/status",
          value: "entered-in-error",
        });

        toast.success("Resultado removido com sucesso!");
      }
      // Se tiver múltiplos resultados e nenhum específico foi informado, remover todos
      else if (row?.resultUrls && row.resultUrls.length > 0) {
        for (const resultUrl of row.resultUrls) {
          const documentReference = await GetDocumentReferenceById({
            patient: user?.username,
            docId: resultUrl?.split("/")?.[1],
            isLogged: true,
            subjectId: user?.username,
          });

          await PatchEHRPoster({
            resourceType: "DocumentReference",
            resourceId: documentReference?.entry?.[0]?.resource?.id || "",
            operation: "replace",
            path: "/status",
            value: "entered-in-error",
          });
        }
        toast.success(
          row.resultUrls.length > 1
            ? "Resultados removidos com sucesso!"
            : "Resultado removido com sucesso!"
        );
      } else if (row?.resultUrl) {
        const documentReference = await GetDocumentReferenceById({
          patient: user?.username,
          docId: row?.resultUrl?.split("/")?.[1],
          isLogged: true,
          subjectId: user?.username,
        });

        await PatchEHRPoster({
          resourceType: "DocumentReference",
          resourceId: documentReference?.entry?.[0]?.resource?.id || "",
          operation: "replace",
          path: "/status",
          value: "entered-in-error",
        });

        toast.success("Resultado removido com sucesso!");
      }

      setRefetch((prev: boolean) => !prev);
    } catch (error) {
      console.error("Erro ao remover resultado(s):", error);
      toast.error("Ocorreu um erro ao remover o(s) resultado(s)");
    }
  }

  return (
    <>
      {useMobileLabeledActions ? (
        <EhrMobileActionStack>
          {/* Input file escondido - compartilhado por todos os botões, só renderiza no browser */}

          {!isRN && (
            <VisuallyHiddenInput
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={(e: any) => uploadImage(e)}
            />
          )}

          {row.resultUrls && row.resultUrls.length > 0 ? (
            <>
              <Button
                fullWidth
                onClick={(e) => {
                  if (row.resultUrls.length > 1) {
                    handleMenuClick(e);
                  } else {
                    const drId = resolveDocumentReferenceIdFromUrl(row.resultUrls[0]);
                    setCommentFromDocId(drId);
                    setBinaryUrl(row.resultUrls[0]);
                    if (isMobileCardLayout) {
                      handleOpen();
                    } else if (isMobile) {
                      downloadBase64File(row?.resultUrls[0]);
                    } else {
                      handleOpen();
                    }
                  }
                }}
                sx={ehrMobileActionBtnSx}
                startIcon={<Visibility />}
                variant="outlined"
                color="primary"
              >
                {row.resultUrls.length > 1
                  ? `Visualizar (${row.resultUrls.length})`
                  : "Visualizar resultado"}
                {row.resultUrls.length > 1 && (
                  <KeyboardArrowDown sx={{ ml: 1 }} />
                )}
              </Button>

              {row?.resultUrls && row.resultUrls.length < 5 && (
                <Button
                  fullWidth
                  onClick={handleClickUpload}
                  startIcon={<FileUpload />}
                  variant="outlined"
                  color="primary"
                  sx={ehrMobileActionBtnSx}
                >
                  Adicionar resultado
                </Button>
              )}

              <Button
                fullWidth
                onClick={() => {
                  setResultToDelete(undefined);
                  setConfirmDeleteDialogOpen(true);
                }}
                startIcon={<Delete />}
                variant="outlined"
                color="error"
                sx={ehrMobileActionBtnSx}
              >
                Remover resultados
              </Button>
            </>
          ) : (
            <Button
              fullWidth
              onClick={(e) => handleClickUpload(e)}
              startIcon={
                isAnalyzingDocument ? (
                  <CircularProgress size={20} />
                ) : (
                  <FileUpload />
                )
              }
              variant="outlined"
              color="primary"
              disabled={isAnalyzingDocument}
              sx={ehrMobileActionBtnSx}
            >
              Enviar resultado
            </Button>
          )}

          {row?.binaryUrl && row?.binaryUrl.trim() !== "" ? (
            <>
              <Button
                fullWidth
                onClick={() => {
                  setCommentFromDocId(row?.fullResource?.id);
                  setBinaryUrl(row?.binaryUrl);
                  if (isMobileCardLayout) {
                    handleOpen();
                  } else if (isMobile) {
                    downloadBase64File(row?.binaryUrl);
                  } else {
                    handleOpen();
                  }
                }}
                startIcon={<Description />}
                variant="contained"
                color="primary"
                sx={ehrMobileActionBtnSx}
              >
                Visualizar solicitação
              </Button>

              <Button
                fullWidth
                onClick={() => {
                  downloadBase64File(row?.binaryUrl);
                }}
                startIcon={<FileDownload />}
                variant="outlined"
                color="primary"
                sx={ehrMobileActionBtnSx}
              >
                Baixar solicitação
              </Button>
            </>
          ) : null}
        </EhrMobileActionStack>
      ) : (
        <TableCell align="center">
          <Stack direction="row" justifyContent="center" position="relative">
            {!isRN && (
              <VisuallyHiddenInput
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={(e: any) => uploadImage(e)}
              />
            )}

            {/* Verifica se existe resultado válido (não null, não undefined, não vazio) */}
            {row.resultUrls && row.resultUrls.length > 0 ? (
              <>
                {isMobile && (
                  <Tooltip title="Remover todos os resultados">
                    <IconButton onClick={() => {
                      setResultToDelete(undefined);
                      setConfirmDeleteDialogOpen(true);
                    }}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
                )}

                <Tooltip
                  title={
                    row.resultUrls.length > 1
                      ? `Visualizar ${row.resultUrls.length} resultados`
                      : "Visualizar resultado"
                  }
                >
                  <IconButton
                    onClick={(e) => {
                      if (row.resultUrls.length > 1) {
                        handleMenuClick(e);
                      } else {
                      const drId = resolveDocumentReferenceIdFromUrl(row.resultUrls[0]);
                      setCommentFromDocId(drId);
                        setBinaryUrl(row.resultUrls[0]);
                        if (isMobile) {
                          downloadBase64File(row?.resultUrls[0]);
                        } else {
                          handleOpen();
                        }
                      }
                    }}
                  >
                    <Visibility />
                  </IconButton>
                </Tooltip>

                <Tooltip
                  title={
                    row?.resultUrls && row.resultUrls.length >= 5
                      ? "Limite de 5 resultados atingido"
                      : "Adicionar resultado"
                  }
                >
                  <span>
                    <IconButton
                      onClick={handleClickUpload}
                      onChange={(e: any) => uploadImage(e)}
                      disabled={
                        isAnalyzingDocument ||
                        (row?.resultUrls && row.resultUrls.length >= 5)
                      }
                    >
                      {isAnalyzingDocument ? (
                        <CircularProgress size={24} color="primary" />
                      ) : (
                        <FileUpload sx={{ color: "primary700.main" }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            ) : (
              // Botão de UPLOAD - aparece quando NÃO existe resultado válido
              <IconButton
                onClick={(e) => handleClickUpload(e)}
                disabled={isAnalyzingDocument}
              >
                <Tooltip title="Enviar resultado">
                  {isAnalyzingDocument ? (
                    <CircularProgress size={24} color="primary" />
                  ) : (
                    <FileUpload sx={{ color: "primary700.main" }} />
                  )}
                </Tooltip>
              </IconButton>
            )}

            {/* Botões de solicitação - só aparecem se houver binaryUrl válido */}
            {row?.binaryUrl && row?.binaryUrl.trim() !== "" && (
              <>
                <Tooltip title="Baixar solicitação">
                  <IconButton
                    onClick={() => {
                      downloadBase64File(row?.binaryUrl);
                    }}
                  >
                    <FileDownload />
                  </IconButton>
                </Tooltip>

                {!isMobile && (
                  <Tooltip title="Visualizar solicitação">
                    <IconButton
                      onClick={() => {
                        setCommentFromDocId(row?.fullResource?.id);
                        setBinaryUrl(row?.binaryUrl);
                        handleOpen();
                      }}
                    >
                      <Description />
                    </IconButton>
                  </Tooltip>
                )}
              </>
            )}

            {access?.type === "professional" &&
              userPractitionerRole?.id ===
                row?.fullServiceResource?.[0]?.resource?.requester?.reference?.split(
                  "/"
                )?.[1] && (
                <Tooltip
                  title={
                    !row?.sign ? "Assinar documento" : "Reemitir documento"
                  }
                >
                  <IconButton
                    onClick={async () => {
                      setLoading(true);
                      const response = await GetBinary({
                        urlBinary: row?.binaryUrl,
                      });
                      setOriginalDocument(response?.data);
                      await generateItem();
                      setSignatureDialogOpen(true);
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={20} />
                    ) : !row?.sign ? (
                      <Edit />
                    ) : (
                      <Autorenew />
                    )}
                  </IconButton>
                </Tooltip>
              )}

            {verifyVisualizer &&
              verifyVisualizer(row?.encounterId) &&
              access?.type === "professional" && (
                <Tooltip title="Visualizar atendimento">
                  <IconButton
                    onClick={() => {
                      openVisualizer(row?.encounterId);
                    }}
                  >
                    <ContentPaste />
                  </IconButton>
                </Tooltip>
              )}
          </Stack>

          {item?.length && !loading && patient ? (
            <>
              <div style={{ display: "none" }}>
                <DocumentVisualization
                  refact
                  setBase64={setBase64}
                  dataPdf={[
                    {
                      patientCPF: patient?.id?.split("-")?.[1],
                      patientData: patient?.name?.[0]?.text || "",

                      professionalData: userPractitionerRole,
                      location: locationData,
                    },
                  ]}
                />
              </div>
            </>
          ) : (
            <></>
          )}
        </TableCell>
      )}

      {signatureDialogOpen && originalDocument ? (
        <SignatureDialog
          visualize={
            base64 && item
              ? (() => {
                  const examKey = Object.keys(base64?.[0] || {}).find((key) =>
                    key?.startsWith("exam")
                  );
                  return examKey
                    ? base64?.[0]?.[examKey]?.base64?.split(",")?.[1]
                    : originalDocument;
                })()
              : originalDocument
          }
          isOpen={signatureDialogOpen}
          handleClose={() => {
            setSignatureDialogOpen(false);
            const indexes = item?.map((e: any) =>
              selectedInterventions?.all.indexOf(e)
            );

            setInterventionsList(
              selectedInterventions?.all?.filter(
                (e: any, index: number) => !indexes?.includes(index)
              )
            );

            setItem([]);
          }}
          handleSignature={handleSignature}
          row={{ sign: "Pendente" }}
          loading={loading}
        />
      ) : (
        <></>
      )}

      {showInvalidDocumentWarning && invalidDocumentBase64 && (
        <InvalidDocumentWarning
          isOpen={showInvalidDocumentWarning}
          onClose={() => {
            console.log("Fechando modal de aviso");
            setShowInvalidDocumentWarning(false);
            if (access?.type !== "professional") {
              setInvalidDocumentBase64(null);
            }
            setIsAnalyzingDocument(false);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
          }}
          isProfessional={access?.type === "professional"}
          fileBase64={invalidDocumentBase64}
        />
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {row?.resultUrls?.reverse()?.map((url: string, index: number) => (
          <MenuItem
            key={index}
            sx={{ display: "flex", justifyContent: "space-between" }}
            onClick={() => {
              const drId = resolveDocumentReferenceIdFromUrl(url);
              setCommentFromDocId(drId);
              setBinaryUrl(url);
              if (isMobileCardLayout) {
                handleOpen();
              } else if (isMobile) {
                downloadBase64File(url);
              } else {
                handleOpen();
              }
              handleMenuClose();
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexGrow: 1,
                cursor: "pointer",
              }}
            >
              <ListItemIcon>
                <Visibility fontSize="small" />
              </ListItemIcon>
              <ListItemText>Resultado {index + 1}</ListItemText>
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {fileBase64 && (
        <ConfirmDocumentDialog
          setRenderButton={() => {}}
          setRefetch={setRefetch}
          fileBase64={fileBase64}
          isOpenDialog={fileBase64}
          onCloseDialog={() => {
            setFileBase64(null);
            setIsAnalyzingDocument(false);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
          }}
          basedOn={row?.fullResource?.basedOn?.filter((el: any) =>
            el.reference?.includes("ServiceRequest")
          )}
          encounterReference={refs}
          data={{
            ...docReferenceData,
            base64: fileBase64?.split(",")?.[1],
            codeValue: "RESULT",
            codeDisplay: "Resultado de exame",
            categoryValue: "resultado",
            categoryDisplay: "Resultado de exame",
            practitionerId: row?.practitionerId,
            description: row?.description,
            organization: objData?.organization,
          }}
          warningMessage={
            fileBase64 === invalidDocumentBase64
              ? "Atenção: Este arquivo pdf parece não ser um resultado de exame. \nDeseja subir assim mesmo?"
              : undefined
          }
        />
      )}

      <DeleteResultsDialog
        open={confirmDeleteDialogOpen}
        onClose={() => setConfirmDeleteDialogOpen(false)}
        resultUrls={row?.resultUrls}
        resultToDelete={resultToDelete}
        onDeleteResult={async (url) => {
          await removeUpload(url);
          setConfirmDeleteDialogOpen(false);
        }}
      />
    </>
  );
}
